import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { AuthSession } from '../types';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3333/api/v1';

const ACCESS_TOKEN_KEY = 'crm.accessToken';
const REFRESH_TOKEN_KEY = 'crm.refreshToken';

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },
  save(accessToken: string, refreshToken: string) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },
  clear() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

export const api = axios.create({ baseURL: BASE_URL });

/**
 * Origem da API (sem /api/v1). Em produção a API mora no mesmo domínio e
 * VITE_API_URL é relativo ("/api/v1"): aí vale a origem da própria página.
 */
export const apiOrigin = (): string =>
  BASE_URL.replace(/\/api\/v1\/?$/, '') || window.location.origin;

api.interceptors.request.use((config) => {
  const token = tokenStore.access;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/** Chamado quando o refresh falha — a UI derruba a sessão. */
let onSessionExpired: (() => void) | null = null;
export const setSessionExpiredHandler = (handler: () => void) => {
  onSessionExpired = handler;
};

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

// Um refresh por vez: requisições paralelas que tomam 401 esperam a mesma renovação.
let refreshInFlight: Promise<string> | null = null;

const refreshSession = async (): Promise<string> => {
  const refreshToken = tokenStore.refresh;
  if (!refreshToken) throw new Error('Sem refresh token');

  // Cliente separado: o interceptor não pode reentrar aqui.
  const { data } = await axios.post<AuthSession>(`${BASE_URL}/auth/refresh`, { refreshToken });
  tokenStore.save(data.accessToken, data.refreshToken);
  return data.accessToken;
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;
    const isAuthCall = config?.url?.includes('/auth/');

    if (error.response?.status !== 401 || !config || config._retried || isAuthCall) {
      return Promise.reject(error);
    }

    config._retried = true;
    try {
      refreshInFlight = refreshInFlight ?? refreshSession();
      const accessToken = await refreshInFlight;
      config.headers.Authorization = `Bearer ${accessToken}`;
      return api.request(config);
    } catch (refreshError) {
      tokenStore.clear();
      onSessionExpired?.();
      return Promise.reject(refreshError);
    } finally {
      refreshInFlight = null;
    }
  },
);

/** Extrai a mensagem de erro que o Nest devolve, com fallback legível. */
export const errorMessage = (error: unknown, fallback = 'Algo deu errado'): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string | string[] } | undefined;
    if (Array.isArray(data?.message)) return data.message.join(', ');
    if (data?.message) return data.message;
    if (error.code === 'ERR_NETWORK') return 'API fora do ar. Rodou `npm run dev:api`?';
  }
  return fallback;
};
